import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User as UserIcon, LogOut, Camera, Settings } from 'lucide-react';
import { useSafeAuth, signOut } from '../hooks/useSafeAuth';
import { db, User } from '../lib/db';
import { useDbUser } from '../hooks/useDbUser';
import { toast } from 'sonner';

/**
 * UserProfileDropdown Component
 * 
 * This component creates a clickable user avatar that opens a dropdown menu
 * with profile information and navigation options. It's like a personal menu
 * for each user that appears in the top navigation bar.
 * 
 * Features:
 * 1. Shows user's profile photo or initials as avatar
 * 2. Displays user information when clicked
 * 3. Allows uploading/changing profile photo
 * 4. Provides links to profile settings and app settings
 * 5. Includes sign out functionality
 * 6. Closes when clicking outside the dropdown
 * 
 * The dropdown shows:
 * - User's name and email
 * - Profile photo (with upload option on hover)
 * - Link to Profile Settings page
 * - Link to App Settings page  
 * - Sign Out button
 */
export function UserProfileDropdown() {
  // Get authentication info (login user data)
  const { user } = useSafeAuth();
  
  // Get database user info (more detailed profile data)
  const { dbUserId, loading: dbUserLoading } = useDbUser();
  
  // Navigation function to change pages
  const navigate = useNavigate();
  
  // State to track if dropdown is open or closed
  const [isOpen, setIsOpen] = useState(false);
  
  // State to store the user's full profile information from database
  const [dbUser, setDbUser] = useState<User | null>(null);
  
  // State to track if we're currently uploading a photo
  const [uploading, setUploading] = useState(false);
  
  // Reference to the dropdown element (for click-outside detection)
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Reference to the hidden file input (for photo uploads)
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load the user's profile data when their database ID becomes available
  useEffect(() => {
    if (dbUserId && !dbUserLoading) {
      loadUserProfile();
    }
  }, [dbUserId, dbUserLoading]);

  // Set up click-outside detection to close dropdown when user clicks elsewhere
  useEffect(() => {
    // Function to handle clicks outside the dropdown
    function handleClickOutside(event: MouseEvent) {
      // If click was outside dropdown, close it
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    // Add event listener when component mounts
    document.addEventListener('mousedown', handleClickOutside);
    
    // Remove event listener when component unmounts (cleanup)
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Function to fetch the user's complete profile from the database
  const loadUserProfile = async () => {
    if (!dbUserId) return;
    
    try {
      // Fetch user profile data from database
      const profile = await db.users.findById(dbUserId);
      if (profile) {
        setDbUser(profile);
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  };

  // Function to handle user signing out
  const handleSignOut = async () => {
    // Clear user session and authentication
    await signOut();
    
    // Redirect to home page after sign out
    navigate('/');
  };

  // Function to handle uploading a new profile photo
  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !dbUser) return;

    // Validate that the selected file is actually an image
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size to prevent huge uploads (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    // Show uploading state
    setUploading(true);
    try {
      // Convert the image file to base64 format for storage
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64String = reader.result as string;
        
        // Save the base64 image data to user's profile in database
        await db.users.update(dbUser.id, {
          profile_photo_url: base64String
        });
        
        // Reload the profile to show the new photo
        await loadUserProfile();
        toast.success('Profile photo updated');
      };
      // Start reading the file as base64
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error('Failed to upload photo');
    } finally {
      // Always hide uploading state when done
      setUploading(false);
    }
  };

  // Function to get the best available display name for the user
  const getUserDisplayName = () => {
    // First preference: use custom display name from profile
    if (dbUser?.display_name) return dbUser.display_name;
    
    // Second preference: use part of email before @ symbol
    if (user?.email) return user.email.split('@')[0];
    
    // Fallback: generic "User" if nothing else available
    return 'User';
  };

  // Function to create user initials for avatar (when no photo available)
  const getInitials = () => {
    const name = getUserDisplayName();
    return name
      .split(' ')                // Split name into words
      .map(part => part[0])      // Take first letter of each word
      .join('')                  // Join letters together
      .toUpperCase()             // Make uppercase
      .slice(0, 2);              // Limit to 2 characters maximum
  };

  return (
    // Main container for the dropdown (positioned relative for absolute dropdown positioning)
    <div className="relative" ref={dropdownRef}>
      {/* Avatar button that toggles the dropdown */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center p-1.5 rounded-full hover:bg-gray-100 transition-colors"
      >
        {/* Show profile photo if available, otherwise show initials */}
        {dbUser?.profile_photo_url ? (
          <img 
            src={dbUser.profile_photo_url} 
            alt={getUserDisplayName()}
            className="w-8 h-8 rounded-full object-cover"
          />
        ) : (
          // Circular avatar with user initials when no photo
          <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center text-white text-sm font-medium">
            {getInitials()}
          </div>
        )}
      </button>

      {/* Dropdown menu - only show when isOpen is true */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
          {/* Profile Section - shows user info and photo */}
          <div className="px-4 py-3 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              {/* Larger avatar with hover overlay for photo upload */}
              <div className="relative group">
                {/* Show profile photo or initials (larger version) */}
                {dbUser?.profile_photo_url ? (
                  <img 
                    src={dbUser.profile_photo_url} 
                    alt={getUserDisplayName()}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-indigo-600 flex items-center justify-center text-white text-lg font-medium">
                    {getInitials()}
                  </div>
                )}
                
                {/* Photo Upload Overlay - appears on hover with camera icon */}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="absolute inset-0 bg-black bg-opacity-50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                >
                  <Camera className="w-5 h-5 text-white" />
                </button>
              </div>
              
              {/* User name and email display */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {getUserDisplayName()}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {user?.email}
                </p>
              </div>
            </div>
          </div>

          {/* Menu Items - navigation options */}
          {/* Profile Settings button */}
          <button
            onClick={() => {
              navigate('/profile');  // Go to profile page
              setIsOpen(false);      // Close dropdown
            }}
            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
          >
            <UserIcon className="w-4 h-4 mr-3" />
            Profile Settings
          </button>
          
          {/* App Settings button */}
          <button
            onClick={() => {
              navigate('/settings');  // Go to settings page
              setIsOpen(false);       // Close dropdown
            }}
            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
          >
            <Settings className="w-4 h-4 mr-3" />
            App Settings
          </button>
          
          {/* Sign Out section (separated with border) */}
          <div className="border-t border-gray-200 mt-2 pt-2">
            <button
              onClick={handleSignOut}
              className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center"
            >
              <LogOut className="w-4 h-4 mr-3" />
              Sign Out
            </button>
          </div>
        </div>
      )}

      {/* Hidden file input for photo uploads */}
      {/* This is invisible but gets triggered when user clicks camera icon */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"              // Only allow image files
        onChange={handlePhotoUpload}  // Handle when user selects a file
        className="hidden"            // Make completely invisible
      />
    </div>
  );
}